import type { KeyboardEvent } from "react";
import { z } from "zod";
import {
  Box,
  Text,
  WidgetInteractive,
  WidgetSkeleton,
  useWidgetInstance,
  type WidgetModule,
  type WidgetRenderContext,
  compactNumber,
  type WidgetSettingField,
  type WidgetSettingOption,
  type WidgetSettingsGroup,
  type WidgetTitleContext,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import type { WidgetSection } from "#app/widgets/dashboards/widget-sections";
import { widgetFiltersSchema } from "#app/widgets/dashboards/widget-filters";
import { useWidgetDrilldown } from "#app/widgets/dashboards/widget-drilldown";
import {
  MISSING_FIELD_TITLE,
} from "#app/widgets/dashboards/use-dimension-slices";
import {
  MEASURE_FIELDS_SOURCE,
  calculateSchema,
  measureFieldId,
  measureSettingFields,
  type MeasureFormat,
  useFieldMeasures,
  useMeasureFormat,
  withLegacyDataKeys,
} from "#app/widgets/dashboards/widget-measure";
import { isMeasureFn, type MeasureFn } from "@salestrak/widgets";
import { WidgetHeaderFilters } from "../shared/widget-header-filters";
import { resolveStoredTitle } from "../shared/derived-titles";
import { useMetric, type MetricMeasure } from "./use-metric";
import { PROPERTY_ICON } from "#app/lib/constants/property-icons";

/** The Units choices: `default` reads the measure's own caption and format. */
const METRIC_UNITS = ["default", "none", "usd", "eur", "gbp", "percent", "custom"] as const;

/**
 * `metric` — a single big-number calculation card (a "stat"): the count of
 * projects in scope, the count with no assignee, or — a number or money field
 * picked straight off the Measure select, then a Calculate row — the sum /
 * average / lowest / highest of that field over the projects in scope. Purely
 * presentational chrome over the shared count reader (`useMetric` folds scope
 * + the card's Filters blob the same way `useDimensionSlices` does). Serves the
 * Custom "Calculation / Metric" preset and the Projects-section counts.
 * The card's own title (rename) names it; the muted caption says what the
 * number is.
 */
export const metricConfigSchema = withLegacyDataKeys(
  z.looseObject({
    /** One of `METRIC_COUNTS`, or a custom field's id. */
    measure: z.string().default("total"),
    calculate: calculateSchema,
    compact: z.boolean().default(false),
    /** A muted line under the figure: the same figure over the previous
     *  equal-length window. Nothing on an all-time window. */
    compare: z.boolean().default(false),
    /** How the figure reads its unit: the measure's own caption and format
     *  (`default`), a bare figure (`none`), a currency or percent symbol on
     *  the figure itself, or a caption the user wrote (`custom`). */
    units: z.enum(METRIC_UNITS).default("default"),
    /** The caption under the figure when `units` is `custom`. */
    unitsLabel: z.string().default(""),
    dateField: z.enum(["creationDate", "lastUpdateDate"]).optional(),
    filters: widgetFiltersSchema.nullish(),
  }),
);

export type MetricConfig = z.infer<typeof metricConfigSchema>;

/** A symbol unit worn by the figure: currencies lead, percent trails. */
const UNIT_SYMBOLS = { usd: "$", eur: "€", gbp: "£", percent: "%" } as const;
type SymbolUnit = keyof typeof UNIT_SYMBOLS;

const UNITS_OPTIONS: WidgetSettingOption[] = [
  { value: "default", label: "Default" },
  { value: "none", label: "None" },
  { value: "usd", label: "$ - Dollar symbol" },
  { value: "eur", label: "€ - Euro symbol" },
  { value: "gbp", label: "£ - Pound symbol" },
  { value: "percent", label: "% - Percentage" },
  { value: "custom", label: "Custom" },
];

/** A figure under a symbol unit: the raw number with the symbol on it. The
 *  measure's own format is bypassed — a money field's currency would collide
 *  with the chosen symbol. */
function formatWithSymbol(value: number, unit: SymbolUnit, compact: boolean): string {
  const number = compact ? compactNumber(value) : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return unit === "percent" ? `${number}${UNIT_SYMBOLS.percent}` : `${UNIT_SYMBOLS[unit]}${number}`;
}

/** The figure and caption a card prints under its Units choice. A symbol unit
 *  moves the unit onto the figure and drops the caption; `none` drops the
 *  caption alone; a blank custom label falls back to the derived caption. */
function applyUnits(
  config: MetricConfig,
  format: MeasureFormat,
  derivedCaption: string,
): { figure: (value: number) => string; caption: string | null } {
  const figure = (value: number) => (config.compact ? format.compact(value) : format.format(value));
  const { units } = config;
  switch (units) {
    case "none":
      return { figure, caption: null };
    case "custom":
      return { figure, caption: config.unitsLabel.trim() || derivedCaption };
    case "default":
      return { figure, caption: derivedCaption };
    default:
      return { figure: (value) => formatWithSymbol(value, units, config.compact), caption: null };
  }
}

/** The metric's count vocabulary — every other Measure value is a field id. */
const METRIC_COUNTS = ["total", "unassigned"] as const;
type MetricCount = (typeof METRIC_COUNTS)[number];

/** The count a Measure value names; an unknown value counts everything. */
function metricCount(measure: string): MetricCount {
  return measure === "unassigned" ? "unassigned" : "total";
}

/** What the reader is asked for: a count, or the field aggregate. */
function metricKind(measure: string): MetricMeasure {
  return measureFieldId(measure, METRIC_COUNTS) === null ? metricCount(measure) : "field";
}

/** The count captions. A field measure's caption comes from `useMeasureFormat` instead. */
const MEASURE_META: Record<MetricCount, { caption: string }> = {
  total: { caption: "Projects" },
  unassigned: { caption: "Unassigned Projects" },
};

/** Title mechanics: the per-measure derived titles are metric-generated and
 *  re-derive when the measure changes; the gallery name ("Calculation"),
 *  curated seed titles ("New this week", "Active Projects") and renames stick. */
const MEASURE_TITLE: Record<MetricCount, string> = {
  total: "Total Projects",
  unassigned: "Total Unassigned",
};
/** Under a field measure the title names the aggregate over the field —
 *  "Total Deductible", "Average Deductible" — and falls back to the generic
 *  "value" when the title context cannot name the field (a field the
 *  workspace no longer has); the caption under the figure names the field. */
const FIELD_MEASURE_PREFIX: Record<MeasureFn, string> = {
  sum: "Total",
  avg: "Average",
  min: "Lowest",
  max: "Highest",
};
const FIELD_NOUN = "value";
/** The Custom card's gallery name, stored as a placed row's title. */
const METRIC_BLANK_NAME = "Calculation";
const GENERATED_METRIC_TITLES = new Set([
  ...Object.values(MEASURE_TITLE),
  ...Object.values(FIELD_MEASURE_PREFIX).map((prefix) => `${prefix} ${FIELD_NOUN}`),
]);

/** The field a metric's Measure names, or null for a count or a field the
 *  context cannot name. */
function measuredFieldName(config: MetricConfig, context: WidgetTitleContext): string | null {
  const fieldId = measureFieldId(config.measure, METRIC_COUNTS);
  return fieldId === null ? null : (context.optionLabel(MEASURE_FIELDS_SOURCE, fieldId) ?? null);
}

function derivedMetricTitle(config: MetricConfig, context: WidgetTitleContext): string {
  if (measureFieldId(config.measure, METRIC_COUNTS) !== null) {
    const prefix = FIELD_MEASURE_PREFIX[isMeasureFn(config.calculate) ? config.calculate : "sum"];
    return `${prefix} ${measuredFieldName(config, context) ?? FIELD_NOUN}`;
  }
  return MEASURE_TITLE[metricCount(config.measure)];
}

/** The titles the metric family generates: the fixed count and generic-value
 *  forms, plus every aggregate over the CURRENT field, so a Calculate change
 *  re-derives. A title committed under a field the card since left behind
 *  cannot be recognized and sticks like a rename. */
function isGeneratedMetricTitle(
  title: string,
  config: MetricConfig,
  context: WidgetTitleContext,
): boolean {
  if (GENERATED_METRIC_TITLES.has(title)) return true;
  const name = measuredFieldName(config, context);
  return name !== null && Object.values(FIELD_MEASURE_PREFIX).some((prefix) => title === `${prefix} ${name}`);
}

function MetricRender({ h, config }: WidgetRenderContext<MetricConfig>) {
  const dateField = config.dateField === "lastUpdateDate" ? "lastUpdateDate" : "creationDate";
  const kind = metricKind(config.measure);
  const { asked, pending } = useFieldMeasures(config.measure, config.calculate, undefined, METRIC_COUNTS);
  const { value, previous, basis, loading, error } = useMetric(
    kind,
    config.filters,
    dateField,
    asked,
    config.compare,
  );
  const format = useMeasureFormat(asked);
  // Click-through: the number opens this card's Data drawer. The card has no
  // discrete elements to drill into, so it opens the full window; an
  // "unassigned" measure narrows every drill of the card itself
  // (`measureWhere`), so the press and the header's Maximize list one set.
  const drill = useWidgetDrilldown();
  const instance = useWidgetInstance();

  if (error) throw error;
  if (loading) {
    return <WidgetSkeleton variant="stat" />;
  }

  // The count caption names what is counted; a field caption names the
  // aggregate; a field not yet chosen asks for one.
  const derivedCaption = kind === "field" ? format.caption : MEASURE_META[kind].caption;
  const units = applyUnits(config, format, derivedCaption);
  // A missing field asks for one regardless of the Units choice.
  const caption = pending ? MISSING_FIELD_TITLE : units.caption;
  const canDrill = Boolean(drill && instance);
  // The comparison reads like the stat strip's: the previous window's figure
  // and the window it names, never a delta — the reader compares two figures.
  const comparison =
    previous !== null && basis && !pending
      ? `vs ${units.figure(previous)} ${basis}`
      : null;
  const openData = () => {
    if (!drill || !instance) return;
    drill.openData(instance.instanceId, null);
  };

  // The card is `bodyDraggable`: its padding ring plus header remain the grab
  // surface, while `WidgetInteractive` fills the body. Pressing the body drills
  // into the data instead of grabbing the card, and hover marks it as clickable.
  // The wash bleeds `xs` back over the body's inset on the sides and the
  // bottom alike, so it keeps an 8px ring to the card's frame on all three
  // edges. On a one-row card the figure, caption and comparison line fill
  // that wash exactly, which is why the stack runs with no gap: the lines'
  // own leading is the only air between them.
  return (
    <Box fill="flex" align="center" justify="center" marginXNegative="xs" marginBottomNegative="xs">
      <WidgetInteractive
        fill="cell"
        rounded="lg"
        align="center"
        justify="center"
        cursor={canDrill ? "pointer" : undefined}
        hoverBackground={canDrill}
        onClick={canDrill ? openData : undefined}
        // The drill is a button for the keyboard and a screen reader too.
        role={canDrill ? "button" : undefined}
        tabIndex={canDrill ? 0 : undefined}
        onKeyDown={canDrill ? (event: KeyboardEvent) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          openData();
        } : undefined}
      >
        <Box align="center" gap="none">
          {/* From four rows the figure stands in a taller line box than its
              own leading (36px glyphs, 58px row), so it holds the eye over
              the caption. A three-row body (~80px) cannot spare it once the
              comparison line is on. */}
          <Box align="center" justify="center" height={h >= 4 ? 58 : undefined}>
            <Text size={h >= 3 ? "4xl" : "2xl"} weight="bold" truncate={1}>
              {/* No figure — no field chosen, or none of the projects hold it.
                  A dash, never a zero: zero is a measurement. */}
              {value === null || pending ? "—" : units.figure(value)}
            </Text>
          </Box>
          {caption !== null ? (
            <Text size="base" variant="muted" truncate={1}>
              {caption}
            </Text>
          ) : null}
          {comparison ? (
            <Text size="2xs" variant="subtle" truncate={1} tabular>
              {comparison}
            </Text>
          ) : null}
        </Box>
      </WidgetInteractive>
    </Box>
  );
}

/** The metric's own Measure select: the two counts, then the workspace's
 *  number and money fields under their type headings. */
const METRIC_MEASURE_FIELD: WidgetSettingField = {
  key: "measure",
  kind: "select",
  label: "Measure",
  icon: "hash",
  options: [
    { value: "total", label: "Total Projects" },
    { value: "unassigned", label: "Unassigned Projects" },
  ],
  dynamicOptions: MEASURE_FIELDS_SOURCE,
};

const metricSettings = (config: MetricConfig): WidgetSettingsGroup[] => [
  {
    title: "Source",
    fields: [{ key: "source", kind: "source", label: "Workflow" }],
  },
  {
    title: "Data",
    fields: measureSettingFields(
      METRIC_MEASURE_FIELD,
      measureFieldId(config.measure, METRIC_COUNTS) !== null,
    ),
  },
  {
    title: "Display",
    fields: [
      { key: "compact", kind: "toggle", label: "Abbreviate large numbers", icon: "minimize" },
      { key: "compare", kind: "toggle", label: "Compare with previous period", icon: "history" },
      { key: "units", kind: "select", label: "Units", icon: "ruler", options: UNITS_OPTIONS },
      ...(config.units === "custom"
        ? [
            {
              key: "unitsLabel",
              kind: "text",
              label: "Custom units",
              placeholder: "e.g. tasks",
              continuation: true,
            } satisfies WidgetSettingField,
          ]
        : []),
    ],
  },
  {
    title: "Filters",
    fields: [{ key: "filters", kind: "issueFilters", label: "Filters" }],
  },
];

export const metricDef: WidgetModule<MetricConfig> = {
  // A lone figure: maximized, the dialog opens on the rows behind it — beside
  // them the body would restate the number the card already showed.
  detailPresentation: "data",
  type: "metric",
  title: "Metric",
  dynamicTitle: (config, context) => {
    // A blank card named after its field says more than the gallery name, and
    // the gallery name is what the placed row stores as its title — so under
    // a named field the family owns that label and re-derives it.
    const fieldNamed = measuredFieldName(config, context) !== null;
    return resolveStoredTitle(
      config,
      (title) => (fieldNamed && title === METRIC_BLANK_NAME) || isGeneratedMetricTitle(title, config, context),
      derivedMetricTitle(config, context),
      fieldNamed ? undefined : METRIC_BLANK_NAME,
    );
  },
  icon: "hash",
  span: { minW: 2, minH: 3, maxW: 12, maxH: 4, defaultW: 3, defaultH: 3 },
  configSchema: metricConfigSchema,
  settings: metricSettings,
  headerActions: WidgetHeaderFilters,
  // A near-empty card: grab it from anywhere; its one number is carved out as
  // the clickable drilldown block (see MetricRender).
  bodyDraggable: true,
  catalog: [
    {
      key: "metric.custom",
      label: METRIC_BLANK_NAME,
      description: "A single big number — a count of Projects, or a sum or average of a field.",
      section: "custom" satisfies WidgetSection,
      tags: ["projects"] satisfies readonly WidgetTag[],
      // Lands named by its gallery label. The label is not the family's while
      // every choice is built in, so it sticks like a rename until the user
      // renames the card or brings a custom field in — then the title derives
      // from the field ("Deductible by Bid Type").
      defaultConfig: { measure: "total" },
      defaultW: 3,
    },
    {
      key: "metric.new-this-week",
      label: "New this week",
      description:
        "Projects created this week, against last week's figure.",
      section: "projects" satisfies WidgetSection,
      tags: ["projects"] satisfies readonly WidgetTag[],
      icon: "sparkles",
      // A curated name outside the derived family: it sticks like a rename.
      defaultConfig: { measure: "total", compare: true, filters: { dateRange: { key: "this-week" } } },
      locks: ["measure"],
      defaultW: 3,
    },
    {
      key: "metric.needs-follow-up",
      label: "Update needed",
      description:
        "Projects whose follow up is Update soon or Stale.",
      section: "projects" satisfies WidgetSection,
      tags: ["projects", "ageHealth"] satisfies readonly WidgetTag[],
      icon: PROPERTY_ICON.followUp,
      defaultConfig: { measure: "total", filters: { query: { logic: "AND", blocks: [{ logic: "AND", filters: [{ field: "age.status", operator: "in", value: ["atRisk", "offTrack"] }] }] } } },
      locks: ["measure"],
      defaultW: 3,
    },
  ],
  render: MetricRender,
};
